import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  getAllClassSubjects,
  setClassSubjects,
  deleteClassSubjectGroup,
  syncClassSubjectsFromMarksAllocation,
  type ClassSubject,
} from "../../../service/class-subject";
import { getAllSystems } from "../../../service/systems";
import { getAllClasses } from "../../../service/classes";
import { getAllCourses } from "../../../service/courses";
import { listGroups } from "../../../service/group";
import { getAllSessions } from "../../../service/sessions";
import {
  selectBranches,
  type Branch,
} from "../../../core/data/redux/slice/branchesSlice";
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice";

// This page manages WHICH SUBJECTS A CLASS TAKES in a session -- RMS's
// `class_subjects`. It is deliberately NOT the same thing as a course
// structure / marks allocation, which says how many marks a subject carries in
// one particular exam. Editing here is upstream of that: dropping a subject
// removes its marks allocation and teaching assignment too, server-side.

type System = { _id: string; name: string; code?: string };
type ClassItem = { _id: string; name: string; system: string };
type Course = { _id: string; title: string; courseIdentifier: string };
type GroupItem = { _id: string; name: string; system: string; classes: string[] };
type SessionItem = { _id: string; title: string; branch?: string };

const SYSTEM_PRIORITY: Record<string, number> = { MATRIC: 1, OLEVEL: 2, AKU: 3, COLLEGE: 4 };
const byPriority = (systems: System[]) =>
  [...systems].sort(
    (a, b) => (SYSTEM_PRIORITY[a.code || ""] ?? 99) - (SYSTEM_PRIORITY[b.code || ""] ?? 99)
  );

// Branch.code is the campus short code (NSB, ANC, FBA...) and is what RMS
// prints on these cards. Prefer it over campusNo, which is a bare number and
// rendered as a meaningless "4". Eon has no code set, so the name still backs it.
// Branch.code is the campus short code (NSB, NNZ, FBA) and is what RMS prints.
// Eon has none set, so it needs a fallback -- and the fallback must not be
// campusNo, which renders as a bare "6" next to everyone else's letters. Derive
// initials from the name instead, skipping filler words, so Eon reads "EON".
//
// String() is not decoration either: Eon's campusNo is stored as a NUMBER while
// every other campus has a string, and calling .substring on a number crashed
// the entire page.
const FILLER = ["the", "a", "an", "of", "and"];
const campusBadge = (branch: { code?: string; campusNo?: string; name?: string }) => {
  if (branch?.code) return String(branch.code).substring(0, 6).toUpperCase();
  const words = String(branch?.name || "")
    .split(/\s+/)
    .filter(w => w && !FILLER.includes(w.toLowerCase()))
    .map(w => w.replace(/[^A-Za-z]/g, ""))
    .filter(Boolean);
  if (words.length) return words[0].substring(0, 4).toUpperCase();
  return branch?.campusNo ? String(branch.campusNo) : "?";
};

const BOARD_BY_SYSTEM_CODE: Record<string, string> = {
  MATRIC: "SINDH",
  OLEVEL: "CAM_OL",
  AKU: "AKU_EB",
  COLLEGE: "BIEK",
};
const boardBadge = (code: string | undefined, name: string) =>
  (code && BOARD_BY_SYSTEM_CODE[code]) ||
  name.replace(/[^A-Za-z]/g, "").substring(0, 6).toUpperCase();

// One card per (session, class, group) -- exactly the uniqueness the backend
// enforces, so a card maps 1:1 to a bulk save.
type Card = {
  key: string;
  sessionId: string;
  sessionTitle: string;
  branch: Branch | undefined;
  system: System | undefined;
  cls: { _id: string; name: string; order?: number };
  group: { _id: string; name: string } | null;
  courses: Course[];
};

type Form = { branchId: string; systemId: string; classId: string; groupId: string };
const EMPTY_FORM: Form = { branchId: "", systemId: "", classId: "", groupId: "" };

const CourseStructuresPage = () => {
  const branches = useSelector(selectBranches);
  const currentSession = useSelector(selectCurrentSession) as SessionItem | null;

  const [rows, setRows] = useState<ClassSubject[]>([]);
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [systems, setSystems] = useState<System[]>([]);
  const [allClasses, setAllClasses] = useState<ClassItem[]>([]);
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [allGroups, setAllGroups] = useState<GroupItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [filterBranch, setFilterBranch] = useState("");
  const [filterClass, setFilterClass] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [picked, setPicked] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Card | null>(null);

  const fetchRows = async () => {
    setLoading(true);
    try {
      // all=true is honoured only for owner/admin; everyone else is scoped to
      // their own campus server-side, so this is safe to always request.
      const res = await getAllClassSubjects({ all: true });
      setRows(res.data || []);
    } catch {
      toast.error("Failed to load class subjects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([
      getAllSystems().then(r => setSystems((r as any).data || [])),
      // The whole catalogue: this page can build for ANY campus via the form,
      // independently of the campus selected in the header. `system` is always
      // populated as an object by the API, so normalise it to an id once here --
      // comparing an object to a string silently empties the Class dropdown.
      getAllClasses({ limit: 200, all: "true" }).then(r =>
        setAllClasses(
          (r.classes || []).map((c: any) => ({
            _id: c._id,
            name: c.name,
            system: c.system?._id || c.system,
          }))
        )
      ),
      getAllCourses().then(r => setAllCourses((r as any).data || [])),
      listGroups().then((r: any) =>
        setAllGroups(
          ((r?.data as any[]) || []).map(g => ({
            _id: g._id,
            name: g.name,
            system: g.system?._id || g.system,
            classes: (g.classes || []).map((c: any) => c?._id || c),
          }))
        )
      ),
      getAllSessions({ all: true, limit: 200 }).then((r: any) =>
        setSessions(
          ((r?.data || r?.sessions || []) as any[]).map(s => ({
            _id: s._id,
            title: s.title,
            branch: s.branch?._id || s.branch,
          }))
        )
      ),
      fetchRows(),
    ]).catch(() => {
      /* individual failures already toast */
    });
  }, []);

  // Sessions are per-campus here, so "the session" depends on which campus the
  // form is pointing at. Match the header session BY TITLE inside that campus
  // ("Aug 2026-2027" exists once per campus), falling back to that campus's most
  // recent session when the titles don't line up.
  const sessionForBranch = (branchId: string): SessionItem | null => {
    const mine = sessions.filter(s => String(s.branch) === String(branchId));
    if (!mine.length) return null;
    const byTitle = currentSession?.title
      ? mine.find(s => s.title === currentSession.title)
      : null;
    return byTitle || mine[mine.length - 1];
  };

  const cards: Card[] = useMemo(() => {
    const map = new Map<string, Card>();
    for (const r of rows) {
      if (!r.session || !r.class || !r.course) continue;
      const groupId = r.classGroup?._id || "none";
      const key = `${r.session._id}|${r.class._id}|${groupId}`;
      if (!map.has(key)) {
        const branchId = String((r.session as any).branch || "");
        map.set(key, {
          key,
          sessionId: r.session._id,
          sessionTitle: r.session.title,
          branch: branches.find(b => b._id === branchId),
          system: systems.find(s => s._id === String((r.class as any).system || "")),
          cls: { _id: r.class._id, name: r.class.name, order: r.class.order },
          group: r.classGroup ? { _id: r.classGroup._id, name: r.classGroup.name } : null,
          courses: [],
        });
      }
      map.get(key)!.courses.push(r.course as Course);
    }
    return Array.from(map.values()).sort((a, b) =>
      (a.branch?.name || "").localeCompare(b.branch?.name || "") ||
      // Class.order (I, II, III... IX, X) -- a.cls.name.localeCompare sorted
      // these as plain strings, so "Class IX" landed right after "Class IV"
      // (alphabetically before "V") instead of after "Class VIII".
      (a.cls.order ?? 0) - (b.cls.order ?? 0) ||
      a.cls.name.localeCompare(b.cls.name)
    );
  }, [rows, branches, systems]);

  const visibleCards = useMemo(
    () =>
      cards.filter(
        c =>
          (!filterBranch || c.branch?._id === filterBranch) &&
          (!filterClass || c.cls._id === filterClass)
      ),
    [cards, filterBranch, filterClass]
  );

  // A campus running several boards (e.g. Naseerabad: Matric + AKU) can have
  // the same class name as SEPARATE documents per board -- "Class IX" shown
  // twice here with no way to tell them apart, same issue already fixed on
  // the Teacher-assignment Class dropdown. card.system already carries the
  // full System doc (see cards' useMemo above), just needs to flow through.
  const classesInCards = useMemo(() => {
    const seen = new Map<string, { name: string; systemName?: string }>();
    cards.forEach(c => seen.set(c.cls._id, { name: c.cls.name, systemName: c.system?.name }));
    return Array.from(seen.entries()).map(([_id, v]) => ({ _id, name: v.name, systemName: v.systemName }));
  }, [cards]);

  const updateForm = (key: keyof Form, value: string) => {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      if (key === "branchId") { next.systemId = ""; next.classId = ""; next.groupId = ""; }
      if (key === "systemId") { next.classId = ""; next.groupId = ""; }
      if (key === "classId") { next.groupId = ""; }
      return next;
    });
  };

  // Only the boards this campus actually runs (Branch.systems); falls back to
  // the whole catalogue when a campus has none assigned yet.
  const systemsForForm = useMemo(() => {
    const branch = branches.find(b => b._id === form.branchId);
    const allowed = branch?.systems?.map((s: any) => s._id);
    return byPriority(allowed?.length ? systems.filter(s => allowed.includes(s._id)) : systems);
  }, [branches, form.branchId, systems]);

  const classesForForm = useMemo(
    () => (form.systemId ? allClasses.filter(c => c.system === form.systemId) : []),
    [allClasses, form.systemId]
  );

  const groupsForForm = useMemo(
    () =>
      form.classId
        ? allGroups.filter(
            g => g.system === form.systemId && g.classes.includes(form.classId)
          )
        : [],
    [allGroups, form.systemId, form.classId]
  );

  const openNew = () => {
    setEditingKey(null);
    setForm(EMPTY_FORM);
    setPicked([]);
    setShowModal(true);
  };

  const openEdit = (card: Card) => {
    setEditingKey(card.key);
    setForm({
      branchId: card.branch?._id || "",
      systemId: card.system?._id || "",
      classId: card.cls._id,
      groupId: card.group?._id || "",
    });
    setPicked(card.courses.map(c => c._id));
    setShowModal(true);
  };

  const toggleCourse = (id: string) =>
    setPicked(prev => (prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]));

  const handleSave = async () => {
    if (!form.branchId || !form.systemId || !form.classId) {
      toast.error("Campus, board system and class are required");
      return;
    }
    const session = sessionForBranch(form.branchId);
    if (!session) {
      toast.error("That campus has no academic session yet — create one first");
      return;
    }
    if (!picked.length) {
      toast.error("Tick at least one subject, or delete the class instead");
      return;
    }
    setSubmitting(true);
    try {
      const res = await setClassSubjects({
        session: session._id,
        class: form.classId,
        classGroup: form.groupId || null,
        courses: picked,
      });
      const dropped = res.cascaded?.courseStructures || 0;
      toast.success(
        dropped
          ? `Saved — also removed ${dropped} marks allocation row${dropped === 1 ? "" : "s"} for dropped subjects`
          : "Subjects saved"
      );
      setShowModal(false);
      setForm(EMPTY_FORM);
      setPicked([]);
      setEditingKey(null);
      fetchRows();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Failed to save subjects");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteClassSubjectGroup({
        session: deleteConfirm.sessionId,
        class: deleteConfirm.cls._id,
        classGroup: deleteConfirm.group?._id || null,
      });
      toast.success("Class removed");
      setDeleteConfirm(null);
      fetchRows();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncClassSubjectsFromMarksAllocation({ all: true });
      if (res.inserted) {
        toast.success(res.message);
        fetchRows();
      } else {
        toast.info(res.message);
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const sortedCourses = useMemo(
    () => [...allCourses].sort((a, b) => a.title.localeCompare(b.title)),
    [allCourses]
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex align-items-start justify-content-between mb-3">
          <div>
            <h4 className="mb-1">Course structures</h4>
            <p className="text-muted small mb-0">
              Assign subjects to a class. Pick campus → board → class → (optional
              group), then tick the subjects this class takes.
            </p>
          </div>
          <div className="d-flex gap-2 flex-shrink-0">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={handleSync}
              disabled={syncing}
              title="Add any subject already configured in Marks allocation that isn't ticked here. Only inserts what's missing — never removes, so it's safe to run again."
            >
              {syncing ? "Syncing…" : "⟳ Sync from Marks allocation"}
            </button>
            <button className="btn btn-dark btn-sm" onClick={openNew}>
              + Add
            </button>
          </div>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label mb-1">Campus filter</label>
            <select
              className="form-select"
              value={filterBranch}
              onChange={e => setFilterBranch(e.target.value)}
            >
              <option value="">— All campuses —</option>
              {branches.map(b => (
                <option key={b._id} value={b._id}>{b.name}</option>
              ))}
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label mb-1">Class filter</label>
            <select
              className="form-select"
              value={filterClass}
              onChange={e => setFilterClass(e.target.value)}
            >
              <option value="">— All classes —</option>
              {classesInCards.map(c => (
                <option key={c._id} value={c._id}>{c.systemName ? `${c.name} (${c.systemName})` : c.name}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-muted py-5">Loading…</div>
        ) : !visibleCards.length ? (
          <div className="text-center text-muted py-5">
            No classes have subjects assigned yet.
            <div className="small mt-1">
              Use <strong>+ Add</strong> to pick a class and tick its subjects.
            </div>
          </div>
        ) : (
          visibleCards.map(card => (
            <div className="card mb-2" key={card.key}>
              <div className="card-body py-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {card.branch && (
                      <span className="badge bg-dark">{campusBadge(card.branch)}</span>
                    )}
                    {card.system && (
                      <span className="badge bg-secondary-transparent text-secondary">
                        {boardBadge(card.system.code, card.system.name)}
                      </span>
                    )}
                    <strong>{card.cls.name}</strong>
                    {card.group && (
                      <span className="badge bg-info-transparent text-info">
                        {card.group.name}
                      </span>
                    )}
                    <span className="text-muted small">
                      ({card.courses.length} subject{card.courses.length === 1 ? "" : "s"})
                    </span>
                    <span className="text-muted small">· {card.sessionTitle}</span>
                  </div>
                  <div className="d-flex gap-3 flex-shrink-0">
                    <button
                      className="btn btn-link p-0 text-primary"
                      onClick={() => openEdit(card)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-link p-0 text-danger"
                      onClick={() => setDeleteConfirm(card)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-1">
                  {card.courses.map(c => (
                    <span
                      key={c._id}
                      className="badge bg-light text-dark border fw-normal"
                    >
                      {c.title}{" "}
                      <span className="text-muted">{c.courseIdentifier}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))
        )}

        {/* Add / edit */}
        {showModal && (
          <>
            <div className="modal-backdrop fade show" />
            <div className="modal fade show d-block" role="dialog">
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {editingKey ? "Edit class subjects" : "New course structure"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowModal(false)}
                    />
                  </div>
                  <div className="modal-body">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Campus</label>
                        <select
                          className="form-select"
                          value={form.branchId}
                          disabled={!!editingKey}
                          onChange={e => updateForm("branchId", e.target.value)}
                        >
                          <option value="">— Select campus —</option>
                          {branches.map(b => (
                            <option key={b._id} value={b._id}>{b.name}</option>
                          ))}
                        </select>
                        {form.branchId && !sessionForBranch(form.branchId) && (
                          <div className="form-text text-danger">
                            This campus has no academic session yet.
                          </div>
                        )}
                        {form.branchId && sessionForBranch(form.branchId) && (
                          <div className="form-text">
                            Session: {sessionForBranch(form.branchId)!.title}
                          </div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Board system</label>
                        <select
                          className="form-select"
                          value={form.systemId}
                          disabled={!form.branchId || !!editingKey}
                          onChange={e => updateForm("systemId", e.target.value)}
                        >
                          <option value="">— Select board —</option>
                          {systemsForForm.map(s => (
                            <option key={s._id} value={s._id}>{s.name}</option>
                          ))}
                        </select>
                        {!form.branchId && (
                          <div className="form-text">Pick a campus first.</div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Class / Grade</label>
                        <select
                          className="form-select"
                          value={form.classId}
                          disabled={!form.systemId || !!editingKey}
                          onChange={e => updateForm("classId", e.target.value)}
                        >
                          <option value="">— Select class —</option>
                          {classesForForm.map(c => (
                            <option key={c._id} value={c._id}>{c.name}</option>
                          ))}
                        </select>
                        {!form.systemId && (
                          <div className="form-text">Pick a board first.</div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Class group (optional)</label>
                        <select
                          className="form-select"
                          value={form.groupId}
                          disabled={!form.classId || !!editingKey}
                          onChange={e => updateForm("groupId", e.target.value)}
                        >
                          <option value="">— No group (default) —</option>
                          {groupsForForm.map(g => (
                            <option key={g._id} value={g._id}>{g.name}</option>
                          ))}
                        </select>
                        {!form.classId && (
                          <div className="form-text">Pick a class first.</div>
                        )}
                      </div>
                    </div>

                    {/* Hidden until a class is chosen, matching RMS, which gates
                        this whole block behind campus+board+class+session being
                        resolved. Showing an empty grid with a "pick a class"
                        note just adds noise to a modal you have to scroll. */}
                    {form.classId && sortedCourses.length > 0 && (
                      <>
                        <hr className="my-3" />

                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <label className="form-label mb-0">
                            Subjects this class takes
                            <span className="text-muted ms-2 small">
                              {picked.length} of {sortedCourses.length} selected
                            </span>
                          </label>
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-light btn-sm"
                              onClick={() => setPicked(sortedCourses.map(c => c._id))}
                            >
                              Select all
                            </button>
                            <button
                              type="button"
                              className="btn btn-light btn-sm"
                              onClick={() => setPicked([])}
                            >
                              Clear
                            </button>
                          </div>
                        </div>

                        <div className="row g-2">
                          {sortedCourses.map(c => (
                            <div className="col-md-4" key={c._id}>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`cs-${c._id}`}
                                  checked={picked.includes(c._id)}
                                  onChange={() => toggleCourse(c._id)}
                                />
                                <label className="form-check-label" htmlFor={`cs-${c._id}`}>
                                  {c.title}{" "}
                                  <span className="text-muted small">
                                    {c.courseIdentifier}
                                  </span>
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {editingKey && (
                      <div className="alert alert-light border text-dark mt-3 mb-0 py-2 small">
                        Unticking a subject also removes its marks allocation and
                        teaching assignment for this class.
                      </div>
                    )}
                  </div>
                  <div className="modal-footer gap-2">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSave}
                      disabled={submitting}
                    >
                      {submitting ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Delete confirm */}
        {deleteConfirm && (
          <>
            <div className="modal-backdrop fade show" />
            <div className="modal fade show d-block" role="dialog">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Remove class subjects</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setDeleteConfirm(null)}
                    />
                  </div>
                  <div className="modal-body">
                    <p className="mb-2">
                      Remove all {deleteConfirm.courses.length} subjects from{" "}
                      <strong>{deleteConfirm.cls.name}</strong>
                      {deleteConfirm.group ? ` (${deleteConfirm.group.name})` : ""} for{" "}
                      {deleteConfirm.sessionTitle}?
                    </p>
                    <p className="text-danger small mb-0">
                      Their marks allocation and teaching assignments for this class
                      are removed too.
                    </p>
                  </div>
                  <div className="modal-footer gap-2">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setDeleteConfirm(null)}
                    >
                      Cancel
                    </button>
                    <button className="btn btn-danger" onClick={handleDelete}>
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CourseStructuresPage;
